import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { CacheProvider } from "@emotion/react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { ThemeProvider as StylesThemeProvider } from "@mui/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { HelmetProvider } from "react-helmet-async";

import Router from "../universal/routes";
import LenisProvider from "../components/common/LenisProvider";
import ScrollToTop from "../components/common/ScrollToTop";
import "lenis/dist/lenis.css";
import createReduxStore from "../universal/createReduxStore";
import { restoreCartFromCookieStorage } from "./../redux/reducers/utils/cart";
import createEmotionCache from "../universal/createEmotionCache";
import theme from "./../universal/theme";
import "../components/app/App.scss";

// Grab the state from a global variable injected into the server-generated HTML
const preloadedState = window.__PRELOADED_STATE__ || { catalogReducer: {} }; // eslint-disable-line no-underscore-dangle

// Allow the passed state to be garbage-collected
delete window.__PRELOADED_STATE__; // eslint-disable-line no-underscore-dangle

// Ugly hack but will do for now
preloadedState.catalogReducer.cart = restoreCartFromCookieStorage();

// Create Redux store with initial state
const store = createReduxStore({ preloadedState });
const emotionCache = createEmotionCache();

function Main() {
  React.useEffect(() => {
    if (document) {
      const jssStyles = document.querySelector("#jss-server-side");
      if (jssStyles) {
        jssStyles.parentElement.removeChild(jssStyles);
      }
    }
  }, []);

  return (
    <LenisProvider>
      <CacheProvider value={emotionCache}>
        <MuiThemeProvider theme={theme}>
          <StylesThemeProvider theme={theme}>
            <CssBaseline enableColorScheme />
            <HelmetProvider>
              <Provider store={store}>
                <BrowserRouter>
                  <ScrollToTop />
                  <Router />
                </BrowserRouter>
              </Provider>
            </HelmetProvider>
          </StylesThemeProvider>
        </MuiThemeProvider>
      </CacheProvider>
    </LenisProvider>
  );
}

if (document){
  const container = document.querySelector("#root");
  if (container) {
    // SSR path: hydrate existing markup. Dev-only/client-only path: createRoot.
    if (container.hasChildNodes()) {
      hydrateRoot(container, <Main />);
    } else {
      createRoot(container).render(<Main />);
    }
  }
} 
